import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const gradientAccumulationComponent: ComponentDef = {
  id: 'gradient-accumulation',
  name: 'Gradient Accumulation',
  description: 'Accumulate gradients over multiple small batches before applying an optimizer step.',
  category: 'training',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'accum_steps', label: 'Accumulation steps', type: 'number', default: 4, min: 1, max: 64, step: 1 },
    {
      name: 'variant',
      label: 'Pattern',
      type: 'select',
      default: 'basic',
      options: [
        { label: 'Basic', value: 'basic' },
        { label: 'With DP', value: 'with_dp' },
        { label: 'With PP', value: 'with_pp' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const accum_steps = Number(params.accum_steps);
    const variant = String(params.variant || 'basic');

    const g = new GraphBuilder();

    if (variant === 'basic') {
      // Basic: accumulate gradients from multiple micro-batches, then single optimizer step
      for (let i = 0; i < accum_steps; i++) {
        g.input(`batch_${i}`, [d_model]);
        g.scale(`grad_${i}`, `batch_${i}`, `grad ${i}`);
      }

      const grads = Array.from({ length: accum_steps }, (_, i) => `grad_${i}`);
      let acc = grads[0];
      for (let i = 1; i < grads.length; i++) {
        const next = `acc_${i}`;
        g.add(next, acc, grads[i]);
        acc = next;
      }
      g.scale('avg', acc, '1 / steps');
      g.scale('step', 'avg', 'Optimizer Step');
      g.output('out', 'step');

    } else if (variant === 'with_dp') {
      // With DP: gradient accumulation + data-parallel all-reduce across devices
      for (let i = 0; i < accum_steps; i++) {
        g.input(`batch_${i}`, [d_model]);
        g.scale(`grad_${i}`, `batch_${i}`, `grad ${i} (local)`);
      }

      const grads = Array.from({ length: accum_steps }, (_, i) => `grad_${i}`);
      let acc = grads[0];
      for (let i = 1; i < grads.length; i++) {
        const next = `acc_${i}`;
        g.add(next, acc, grads[i]);
        acc = next;
      }
      g.scale('avg', acc, '1 / steps');

      // All-reduce across DP ranks to sync gradients
      g.allreduce('allreduce', 'avg', [d_model]);
      g.scale('step', 'allreduce', 'Optimizer Step (synced)');
      g.output('out', 'step');

    } else {
      // With PP: gradient accumulation across pipeline stages + micro-batches
      for (let i = 0; i < accum_steps; i++) {
        g.input(`micro_batch_${i}`, [d_model]);

        // Forward through pipeline stages
        g.linear(`stage0_${i}`, `micro_batch_${i}`, [d_model, d_model]);
        g.linear(`stage1_${i}`, `stage0_${i}`, [d_model, d_model]);
        g.scale(`grad_${i}`, `stage1_${i}`, `grad ${i} (pipeline)`);
      }

      const grads = Array.from({ length: accum_steps }, (_, i) => `grad_${i}`);
      let acc = grads[0];
      for (let i = 1; i < grads.length; i++) {
        const next = `acc_${i}`;
        g.add(next, acc, grads[i]);
        acc = next;
      }
      g.scale('avg', acc, '1 / steps');
      g.scale('step', 'avg', 'Optimizer Step (PP)');
      g.output('out', 'step');
    }

    return g.build();
  }
};
